import { useMemo, useState, useCallback, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useReactTable, getCoreRowModel, flexRender } from '@tanstack/react-table'
import type { RunbookTask } from '@/types/entities'
import { createRunbookColumns } from '../columns-runbook'
import { calculateRunbookTimes } from '../utils/calculateRunbookTimes'
import { formatMinutesToDHM } from '../utils/formatTime'
import { RUNBOOK_MODES, SELECTABLE_METHODS, AutomationOptionLabel, AutomationBadge, AutomationSummary, findMode } from '../utils/runbookAutomation'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import { Plus, Save, RotateCcw, ChevronDown, Pencil, Trash2, Code2, Image as ImageIcon, Timer, GitBranch, FileCode } from 'lucide-react'
import { AiSparkle } from '@/components/AiSparkle'
import { TaskCodeFiles, TaskCodeDialog, type TaskCodeSetLike } from './TaskCodeViewer'
import { TranslatableText } from '@/components/ui/TranslatableText'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { validateDependencies } from '../utils/validateDependencies'
import { CardTableViewToggle } from '@/components/ui/card-table-view-toggle'
import { TaskImagePicker } from './TaskImagePicker'
import { taskImageSrc } from '../lib/taskImage'
import { MethodSuggestionBanner } from './MethodSuggestionBanner'
import { OptionGroupPicker, useOptionVocabulary, deriveOptionFromMode } from './OptionGroupPicker'
import {
    buildRunbookEntries,
    countDecisions,
    runbookDurationRange,
    OptionGroupBlock,
    DecisionBadge,
} from '../utils/runbookOptions'

// Solid, bordered task-number circle coloured by task STATUS (solid so the timeline line behind
// it is fully covered). A plan task with no status is "not started" → neutral grey.
function cardCircleClass(taskStatus?: string | null): string {
    switch ((taskStatus || '').toLowerCase()) {
        case 'completed': return 'border-emerald-600 bg-emerald-500 text-white'
        case 'started':
        case 'inprogress': return 'border-blue-600 bg-blue-500 text-white'
        case 'issue': return 'border-red-600 bg-red-500 text-white'
        case 'waiting': return 'border-amber-600 bg-amber-500 text-white'
        // Skipped: the branch not taken. Visibly present, visibly not in play.
        case 'skipped': return 'border-gray-400 bg-gray-100 text-gray-400'
        default: return 'border-gray-500 bg-gray-300 text-gray-700'
    }
}

interface RunbookTableProps {
    tasks: RunbookTask[]
    onSave: (tasks: RunbookTask[]) => void
    autoUpdate: boolean
    onAutoUpdateChange: (autoUpdate: boolean) => void
    isSaving?: boolean
    hideToolbar?: boolean // Hide save/auto-update controls (for form context)
    onReplaceWithMaster?: () => void // Handler for replace with master option
    onCloneRunbook?: () => void // Handler for clone runbook option
    onSaveAsMaster?: () => void // Handler for save as master option
    readOnly?: boolean // Hide actions column and make fields read-only
    /** When set, show a one-off Method recommendation for this runbook (object tier). */
    suggestionObjectId?: string | null
    /** When set, show a one-off Method recommendation for this runbook (master tier). */
    suggestionMasterId?: number | null
    /** Master tier only: called with a task's number to AI-generate its script. */
    onGenerateCode?: (taskNo: number) => void
    /**
     * Scripts that exist for these tasks, keyed by task number. Supplied directly when they
     * were generated this session and are not saved yet (the generator returns them with the
     * runbook), which is the case where nothing else can find them.
     */
    taskCode?: Record<number | string, TaskCodeSetLike> | null
    /** Loads a saved task's script on demand. Ignored for a task already in `taskCode`. */
    onFetchTaskCode?: (taskNo: number) => Promise<TaskCodeSetLike | null>
    /**
     * Object tier only: opens the two AI options for a runbook that came from a master
     * one — regenerate it against this object's real vault content, or keep it and fill
     * in the placeholders. Absent on master runbooks, which have no object to be
     * specific about.
     */
    onAiAssist?: () => void
    /**
     * Task numbers that have a stored script. Inserting renumbers everything below it and
     * a script is keyed by task NUMBER, so an insert while edits are pending can leave a
     * script on the wrong task. With no scripts there is nothing to protect, so the
     * editor stays out of the way.
     */
    codeTaskNos?: number[]
    /**
     * Fires on EVERY committed change, whatever the save mode. `onSave` is the persist
     * channel and is gated by autoUpdate; a caller that also has to keep form state in
     * step needs one that is not, or turning auto-update off silently strands the edits
     * and the next save writes stale tasks.
     */
    onTasksChange?: (tasks: RunbookTask[]) => void
}

/**
 * A script for ONE task is offered only when it is a manual task whose commands are too long to
 * paste (Andrew, 14 Oct 2026) - the server's rule, needsTaskScript in
 * backend/src/services/recovery/code-generation.js: more than 20 lines or 2,000 characters.
 */
export function commandsAreLong(task: { mode?: string | null; commands?: string | null }): boolean {
    if ((task.mode || 'manual').toLowerCase() !== 'manual') return false
    const commands = task.commands || ''
    if (/(^|\n)\s*vsrecovery\s+/.test(commands)) return false
    return commands.split(/\r?\n/).filter((l) => l.trim()).length > 20 || commands.length > 2000
}

export function RunbookTable({
    tasks,
    onSave,
    autoUpdate,
    onAutoUpdateChange,
    isSaving = false,
    hideToolbar = false,
    onReplaceWithMaster,
    onCloneRunbook,
    onSaveAsMaster,
    readOnly = false,
    suggestionObjectId = null,
    suggestionMasterId = null,
    onGenerateCode,
    taskCode = null,
    onFetchTaskCode,
    onTasksChange,
    codeTaskNos,
    onAiAssist,
}: RunbookTableProps) {
    const { t } = useTranslation()
    // Needed here as well as in the picker: changing Automation has to move the task onto
    // the matching branch, and that lookup lives in the vocabulary.
    const { data: optionVocabulary = [] } = useOptionVocabulary()
    const [viewMode, setViewMode] = useState<'card' | 'table'>('card')
    const [editingTaskIndex, setEditingTaskIndex] = useState<number | null>(null)
    const [deleteConfirmIndex, setDeleteConfirmIndex] = useState<number | null>(null)
    const [dependenciesError, setDependenciesError] = useState<string | null>(null)
    const [localTasks, setLocalTasks] = useState<RunbookTask[]>(tasks || [])
    const [imagePickerIndex, setImagePickerIndex] = useState<number | null>(null)
    const [imagePickerOpen, setImagePickerOpen] = useState(false)
    /**
     * Edits made since the last save, when saving is manual.
     *
     * Inserting a task renumbers everything below it, and a generated script is stored
     * against a task NUMBER. Insert while an edit is pending and the renumber lands on
     * the server before the edit does, so the script ends up on the wrong task. Blocking
     * the insert is cheaper than reconciling that afterwards.
     */
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
    // Blocked only when a renumber could actually strand a script. Most runbooks have
    // none, and blocking those made the control look broken for no benefit — which is
    // exactly how it read after someone added a task by mistake and deleted it again.
    const insertBlocked = hasUnsavedChanges && (codeTaskNos?.length ?? 0) > 0

    // Which task is showing its files in the dialog (card view — the edit panel shows them inline).
    const [codeDialogTaskNo, setCodeDialogTaskNo] = useState<number | null>(null)

    /**
     * The script for a task, if we already hold it. Two sources, deliberately: a runbook
     * generated moments ago carries its scripts in memory and has no id to fetch them by,
     * while a saved one has them in the code store. codeTaskNos is the cheap listing that
     * says a saved task HAS code without loading it.
     */
    const codeForTask = useCallback((taskNo: number | null | undefined): TaskCodeSetLike | null => {
        if (taskNo == null || !taskCode) return null
        return taskCode[taskNo] ?? taskCode[String(taskNo)] ?? null
    }, [taskCode])

    const taskHasCode = useCallback((taskNo: number | null | undefined): boolean => {
        if (taskNo == null) return false
        if (codeForTask(taskNo)) return true
        return (codeTaskNos ?? []).includes(taskNo)
    }, [codeForTask, codeTaskNos])

    // Refs to prevent useEffect from overwriting local edits
    const tasksInitializedRef = useRef(false)
    const isInitialMountRef = useRef(true)

    // Keep latest values in refs for stable callbacks
    const localTasksRef = useRef(localTasks)
    useEffect(() => {
        localTasksRef.current = localTasks
    }, [localTasks])

    const onSaveRef = useRef(onSave)
    useEffect(() => {
        onSaveRef.current = onSave
    }, [onSave])

    const onTasksChangeRef = useRef(onTasksChange)
    useEffect(() => {
        onTasksChangeRef.current = onTasksChange
    }, [onTasksChange])

    const autoUpdateRef = useRef(autoUpdate)
    useEffect(() => {
        autoUpdateRef.current = autoUpdate
        // Turning auto-update on persists from here, so nothing is pending any more.
        if (autoUpdate) setHasUnsavedChanges(false)
    }, [autoUpdate])

    const viewModeRef = useRef(viewMode)
    useEffect(() => {
        viewModeRef.current = viewMode
    }, [viewMode])

    // Read by handleDeleteConfirm, which has no dependencies and so cannot close over the
    // state directly. This ref was being read without ever being declared — deleting a
    // task threw a ReferenceError, which aborted the rest of the handler and took the
    // surrounding React tree down with it.
    const editingTaskIndexRef = useRef(editingTaskIndex)
    useEffect(() => {
        editingTaskIndexRef.current = editingTaskIndex
    }, [editingTaskIndex])

    // Use the reusable calculateRunbookTimes utility function

    // Renumber tasks and update dependencies
    const renumberTasks = useCallback((taskList: RunbookTask[]): RunbookTask[] => {
        // Create mapping from old task numbers to new positions
        // First pass: map old task numbers to new positions
        const oldToNewMap = new Map<number, number>()
        taskList.forEach((task, newIndex) => {
            if (task.taskNo !== null) {
                oldToNewMap.set(task.taskNo, newIndex + 1)
            }
        })

        // Renumber tasks and update dependencies
        return taskList.map((task, index) => {
            const newTaskNo = index + 1

            if (index === 0) {
                // Task 1 has no dependencies
                return { ...task, taskNo: newTaskNo, dependencies: '' }
            }

            // Map old dependency task numbers to new ones
            const oldDeps = (task.dependencies || '').split(',').map(d => d.trim()).filter(d => d !== '')
            const newDeps = oldDeps
                .map(dep => {
                    const depNum = parseInt(dep, 10)
                    if (isNaN(depNum)) return ''
                    const newDepNum = oldToNewMap.get(depNum)
                    return newDepNum ? newDepNum.toString() : ''
                })
                .filter(d => d !== '')

            // Default dependency is the previous task if no valid dependencies
            // For tasks that had dependencies but they couldn't be mapped, use previous task
            const defaultDep = index.toString() // Previous task is at index - 1, which is taskNo = index
            const finalDeps = newDeps.length > 0 ? newDeps.join(', ') : defaultDep

            return {
                ...task,
                taskNo: newTaskNo,
                dependencies: finalDeps,
            }
        })
    }, [])

    const renumberTasksRef = useRef(renumberTasks)
    useEffect(() => {
        renumberTasksRef.current = renumberTasks
    }, [renumberTasks])

    /**
     * Apply SEVERAL fields to one task in a single update.
     *
     * This exists because localTasksRef is refreshed by an effect, i.e. after render. Two
     * handleTaskChange calls in a row therefore both read the same stale snapshot, and the
     * second one — built from that snapshot — silently discards the first. Any control that
     * sets two fields at once (option group + option, mode + method) was losing one of them.
     */
    const handleTaskPatch = useCallback((index: number, patch: Partial<RunbookTask>, saveImmediately = true) => {
        const currentTasks = localTasksRef.current
        const updated = [...currentTasks]
        updated[index] = { ...updated[index], ...patch }

        // Duration and dependencies move the timeline, so the offsets are recomputed.
        const touchesTiming = 'duration' in patch || 'dependencies' in patch
        const next = touchesTiming ? calculateRunbookTimes(updated) : updated

        // Mark that we're doing a local edit to prevent useEffect from overwriting
        isInitialMountRef.current = false
        tasksInitializedRef.current = true
        setLocalTasks(next)

        if (saveImmediately) onTasksChangeRef.current?.(next)
        if (saveImmediately && autoUpdateRef.current) {
            onSaveRef.current(next)
        } else if (saveImmediately) {
            setHasUnsavedChanges(true)
        }
    }, []) // No dependencies - uses refs

    const handleTaskChange = useCallback((index: number, field: keyof RunbookTask, value: any, saveImmediately = true) => {
        // Intercept image picker trigger
        if (field === 'imagePath' && value === '__PICK__') {
            setImagePickerIndex(index)
            setImagePickerOpen(true)
            return
        }
        handleTaskPatch(index, { [field]: value } as Partial<RunbookTask>, saveImmediately)
    }, [handleTaskPatch])

    const handleAddTask = useCallback(() => {
        const currentTasks = localTasksRef.current
        const lastTask = currentTasks[currentTasks.length - 1]
        const newTaskNo = currentTasks.length + 1
        const defaultDep = lastTask?.taskNo?.toString() || ''

        const newTask: RunbookTask = {
            taskNo: newTaskNo,
            task: '',
            commands: '',
            duration: 5, // Default 5m for new tasks
            startTime: null,
            finishTime: null,
            dependencies: defaultDep,
            mode: 'manual',
            method: 'manual',
        }

        const updated = [...currentTasks, newTask]
        const withTimes = calculateRunbookTimes(updated)

        // Mark that we're doing a local edit to prevent useEffect from overwriting
        isInitialMountRef.current = false
        tasksInitializedRef.current = true

        setLocalTasks(withTimes)

        onTasksChangeRef.current?.(withTimes)
        if (autoUpdateRef.current) {
            onSaveRef.current(withTimes)
        } else {
            setHasUnsavedChanges(true)
        }

        // Open new task in edit mode when in card view
        if (viewMode === 'card') {
            setEditingTaskIndex(withTimes.length - 1)
        }
    }, [viewMode])

    const handleInsertTask = useCallback((index: number) => {
        const currentTasks = localTasksRef.current

        // Determine the dependency for the new task
        // It should depend on the task that will be before it after insertion
        // (which is the task currently at index - 1)
        const taskBefore = currentTasks[index - 1]
        const defaultDep = taskBefore?.taskNo?.toString() || ''

        const newTask: RunbookTask = {
            taskNo: null, // Will be renumbered
            task: '',
            commands: '',
            duration: 5, // Default 5m for new tasks
            startTime: null,
            finishTime: null,
            dependencies: defaultDep, // Set to the task number that will be before it
            mode: 'manual',
            method: 'manual',
        }

        const updated = [...currentTasks]
        updated.splice(index, 0, newTask)
        const renumbered = renumberTasksRef.current(updated)

        // After renumbering, we need to:
        // 1. Set the inserted task's dependency (depends on the task before it)
        // 2. Update the task that was originally at the insertion point (now at index + 1)
        //    to depend on the newly inserted task
        const finalTasks = renumbered.map((task, idx) => {
            if (idx === index) {
                // This is the newly inserted task at position index (0-based)
                // It should depend on the previous task at position index - 1
                // After renumbering, that task has taskNo = index (1-based, since position index-1 = taskNo index)
                const prevTaskNo = index.toString()
                return { ...task, dependencies: prevTaskNo }
            } else if (idx === index + 1) {
                // This is the task that was originally at the insertion point (now shifted down)
                // It should depend on the newly inserted task (which is now at position index, taskNo = index + 1)
                const insertedTaskNo = (index + 1).toString()
                return { ...task, dependencies: insertedTaskNo }
            }
            return task
        })

        const withTimes = calculateRunbookTimes(finalTasks)

        // Mark that we're doing a local edit to prevent useEffect from overwriting
        isInitialMountRef.current = false
        tasksInitializedRef.current = true

        setLocalTasks(withTimes)

        onTasksChangeRef.current?.(withTimes)
        if (autoUpdateRef.current) {
            onSaveRef.current(withTimes)
        } else {
            setHasUnsavedChanges(true)
        }

        if (viewModeRef.current === 'card') {
            setEditingTaskIndex(index)
        }
    }, []) // No dependencies - uses refs

    const handleDeleteTask = useCallback((index: number) => {
        const currentTasks = localTasksRef.current
        const updated = currentTasks.filter((_, i) => i !== index)
        const renumbered = renumberTasksRef.current(updated)
        const withTimes = calculateRunbookTimes(renumbered)
        setLocalTasks(withTimes)

        onTasksChangeRef.current?.(withTimes)
        if (autoUpdateRef.current) {
            onSaveRef.current(withTimes)
        } else {
            setHasUnsavedChanges(true)
        }

        setDeleteConfirmIndex(null)
        const editing = editingTaskIndexRef.current
        if (editing === index) setEditingTaskIndex(null)
        else if (editing != null && editing > index) setEditingTaskIndex(editing - 1)
    }, [])

    // Add _index to tasks for row identification
    const tasksWithIndex = useMemo(() => {
        if (!localTasks || localTasks.length === 0) return []
        return localTasks.map((task, index) => ({ ...task, _index: index }))
    }, [localTasks])

    const { i18n } = useTranslation()
    const columns = useMemo(() => {
        return createRunbookColumns(
            handleTaskChange,
            handleTaskPatch,
            handleAddTask,
            handleInsertTask,
            handleDeleteTask,
            t,
            i18n.language,
            tasksWithIndex,
            readOnly
        )
    }, [handleTaskChange, handleTaskPatch, handleAddTask, handleInsertTask, handleDeleteTask, t, i18n.language, tasksWithIndex, readOnly])

    const table = useReactTable({
        data: tasksWithIndex,
        columns,
        getCoreRowModel: getCoreRowModel(),
    })

    // Reset handler: discard local changes and reload from database
    const handleReset = useCallback(() => {
        if (tasks && tasks.length > 0) {
            setLocalTasks([...tasks]) // Create a new array to trigger re-render
            tasksInitializedRef.current = true
            isInitialMountRef.current = false
        }
        setHasUnsavedChanges(false)
    }, [tasks])

    // Sync local tasks with props ONLY on initial mount
    // After that, all work is done in localTasks state (in memory)
    // We ignore prop changes after initial load to prevent resetting calculated times
    useEffect(() => {
        // Only sync on initial mount when we first receive tasks from the server.
        // After that, NEVER sync from props — all work is done in localTasks, so the
        // parent's query updates don't reset our calculated times.
        if (isInitialMountRef.current && tasks && tasks.length > 0 && localTasks.length === 0) {
            setLocalTasks(tasks)
            tasksInitializedRef.current = true
            isInitialMountRef.current = false
        }
    }, [tasks])

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
            setEditingTaskIndex(null)
            setDependenciesError(null)
        }
        }
        const handleClickOutside = (e: MouseEvent) => {
            if (editingTaskIndex !== null) {
                const target = e.target as HTMLElement
                // Radix renders Select/dropdown content in a PORTAL on document.body, so a
                // click on an option is physically outside [data-editing-card] even though
                // it belongs to a control inside the card. Without this the card collapsed
                // the moment you picked an Automation or Method value.
                const inPortalContent = target.closest(
                    '[data-radix-popper-content-wrapper],[role="listbox"],[role="option"]'
                )
                if (!target.closest('[data-editing-card]') && !inPortalContent) {
                    setEditingTaskIndex(null)
                    setDependenciesError(null)
                }
            }
        }
        document.addEventListener('keydown', handleKeyDown)
        document.addEventListener('mousedown', handleClickOutside)
        return () => {
            document.removeEventListener('keydown', handleKeyDown)
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [editingTaskIndex])

    const handleDeleteConfirm = useCallback(() => {
        if (deleteConfirmIndex !== null) {
            handleDeleteTask(deleteConfirmIndex)
            setDeleteConfirmIndex(null)
        }
    }, [deleteConfirmIndex, handleDeleteTask])

    /**
     * One task card. Extracted from the flat list so the card view can be driven by
     * option groups (a decision block wraps the cards of its branches) without the
     * card markup being duplicated per branch.
     */
    const renderTaskCard = (task: RunbookTask & { _index?: number }, idx: number) => (
                                <div
                                    key={task.id ?? idx}
                                    className="relative flex gap-4 items-start group"
                                >
                                    {/* Timeline node with insert above button on hover */}
                                    <div className="flex-shrink-0 flex flex-col items-center">
                                        {!readOnly && (
                                            <button
                                                type="button"
                                                onClick={() => handleInsertTask(idx)}
                                                disabled={insertBlocked}
                                                className={`w-6 h-6 rounded-full text-white flex items-center justify-center transition-all opacity-0 group-hover:opacity-100 -mb-1 z-10 ${
                                                    insertBlocked
                                                        ? 'bg-gray-300 cursor-not-allowed'
                                                        : 'bg-blue-500 hover:bg-blue-600'
                                                }`}
                                                title={insertBlocked
                                                    ? t('objects.runbook.insertBlocked', 'Save your changes before inserting a task — inserting renumbers the tasks below it')
                                                    : (t('objects.runbook.insertAbove') || 'Insert task above')}
                                                aria-label={t('objects.runbook.insertAbove') || 'Insert task above'}
                                            >
                                                <Plus className="h-3.5 w-3.5" />
                                            </button>
                                        )}
                                        <div className={`flex-shrink-0 w-10 h-10 rounded-full border-2 flex items-center justify-center text-sm font-semibold ${cardCircleClass(task.taskStatus)}`}>
                                            {task.taskNo ?? '-'}
                                        </div>
                                    </div>
                                    <div
                                        data-editing-card={editingTaskIndex === idx ? '' : undefined}
                                        className={`flex-1 min-w-0 rounded-xl border-2 p-4 transition-all ${
                                            editingTaskIndex === idx
                                                ? 'border-blue-300 ring-2 ring-blue-200 bg-white'
                                                : 'border-gray-200 bg-white'
                                        }`}
                                    >
                                        {editingTaskIndex === idx && !readOnly ? (
                                            /* Edit mode: Task, Commands, Duration, Dependencies */
                                            <div className="space-y-4">
                                                <div>
                                                    <label className="text-xs text-gray-500 block mb-1">{t('objects.runbook.task')}</label>
                                                    <Textarea
                                                        value={task.task || ''}
                                                        onChange={(e) => handleTaskChange(idx, 'task', e.target.value, false)}
                                                        onBlur={(e) => handleTaskChange(idx, 'task', e.target.value)}
                                                        className="min-h-[80px] text-sm"
                                                        placeholder={t('objects.runbook.task')}
                                                        autoFocus
                                                    />
                                                </div>
                                                <div>
                                                    <label className="text-xs text-gray-500 block mb-1">{t('objects.runbook.commands')}</label>
                                                    <Textarea
                                                        value={task.commands || ''}
                                                        onChange={(e) => handleTaskChange(idx, 'commands', e.target.value, false)}
                                                        onBlur={(e) => handleTaskChange(idx, 'commands', e.target.value)}
                                                        className="min-h-[80px] text-sm font-mono resize-y"
                                                        placeholder={t('objects.runbook.commands') || 'Enter commands, one per line...'}
                                                    />
                                                </div>
                                                {/* Automation — sits directly under Commands because it describes how
                                                    that command payload is run, not when the task is scheduled. */}
                                                <div className="flex flex-wrap gap-4">
                                                    <div>
                                                        <label className="text-xs text-gray-500 block mb-1">{t('objects.runbook.automation')}</label>
                                                        <Select
                                                            value={task.mode || 'manual'}
                                                            onValueChange={(v) => {
                                                                // Where the group's branches ARE the automation choice, the
                                                                // branch follows the mode — otherwise the card would show a
                                                                // Script branch on a task the author just set to Manual.
                                                                const vocab = optionVocabulary.find((g) => g.name === (task.optionGroup || ''))
                                                                const branch = deriveOptionFromMode(vocab, v)
                                                                handleTaskPatch(idx, {
                                                                    mode: v,
                                                                    // A manual task has no handler; keep Method consistent.
                                                                    ...(v === 'manual' ? { method: 'manual' } : {}),
                                                                    ...(branch ? { optionName: branch } : {}),
                                                                })
                                                            }}
                                                        >
                                                            {/* The trigger renders the label itself rather than
                                                                using SelectValue: SelectValue clones the SELECTED
                                                                ITEM'S children, so the explanatory line added to each
                                                                option leaked into the collapsed control and overlapped
                                                                the field label. */}
                                                            <SelectTrigger className="w-[190px] h-8 text-sm whitespace-nowrap">
                                                                <AutomationOptionLabel
                                                                    option={findMode(task.mode)}
                                                                    label={t(findMode(task.mode).labelKey)}
                                                                />
                                                            </SelectTrigger>
                                                            <SelectContent>
                                                                {RUNBOOK_MODES.map((m) => (
                                                                    <SelectItem key={m.value} value={m.value}>
                                                                        <span className="flex flex-col">
                                                                            <AutomationOptionLabel option={m} label={t(m.labelKey)} />
                                                                            {m.descKey && (
                                                                                <span className="text-xs text-muted-foreground pl-5">{t(m.descKey)}</span>
                                                                            )}
                                                                        </span>
                                                                    </SelectItem>
                                                                ))}
                                                            </SelectContent>
                                                        </Select>
                                                    </div>
                                                    <div>
                                                        <label className="text-xs text-gray-500 block mb-1">{t('objects.runbook.method')}</label>
                                                        <Select
                                                            value={task.method || 'manual'}
                                                            disabled={(task.mode || 'manual') === 'manual'}
                                                            onValueChange={(v) => handleTaskChange(idx, 'method', v)}
                                                        >
                                                            <SelectTrigger className="w-[190px] h-8 text-sm">
                                                                <SelectValue />
                                                            </SelectTrigger>
                                                            <SelectContent>
                                                                {SELECTABLE_METHODS.map((m) => (
                                                                    <SelectItem key={m.value} value={m.value}>
                                                                        <AutomationOptionLabel option={m} label={t(m.labelKey)} />
                                                                    </SelectItem>
                                                                ))}
                                                            </SelectContent>
                                                        </Select>
                                                    </div>
                                                </div>
                                                {/* Option group — the OR decision. Blank on an
                                                    ordinary task, which is nearly all of them. */}
                                                <div className="flex flex-wrap gap-4">
                                                    <OptionGroupPicker
                                                        group={task.optionGroup}
                                                        option={task.optionName}
                                                        mode={task.mode}
                                                        onChange={({ optionGroup, optionName }) => handleTaskPatch(idx, {
                                                            optionGroup,
                                                            optionName,
                                                            // A task with no branch cannot be the planned one.
                                                            ...(optionGroup && optionName ? {} : { optionDefault: false }),
                                                        })}
                                                    />
                                                    <div className="flex items-end pb-1">
                                                        <div className="flex items-center gap-2">
                                                            <Switch
                                                                id={`option-default-${idx}`}
                                                                checked={!!task.optionDefault}
                                                                disabled={!task.optionGroup || !task.optionName}
                                                                onCheckedChange={(v) => handleTaskChange(idx, 'optionDefault', v)}
                                                            />
                                                            <Label htmlFor={`option-default-${idx}`} className="text-xs cursor-pointer">
                                                                {t('objects.runbook.optionDefault', 'Planned branch')}
                                                            </Label>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="flex flex-wrap gap-4">
                                                    {/* Master-tier only: a script for this one task, when its commands are too long to paste. */}
                                                    {onGenerateCode && task.taskNo != null && commandsAreLong(task) && (
                                                        <div className="flex items-end">
                                                            <Button
                                                                type="button"
                                                                variant="outline"
                                                                size="sm"
                                                                className="h-8 border-purple-300 text-purple-700 hover:bg-purple-100 dark:text-purple-300"
                                                                onClick={() => onGenerateCode(task.taskNo as number)}
                                                            >
                                                                <AiSparkle size={14} className="mr-1" />
                                                                {taskHasCode(task.taskNo)
                                                                    ? t('objects.runbook.regenerateScript', 'Regenerate script')
                                                                    : t('objects.runbook.generateScript', 'Generate script')}
                                                            </Button>
                                                        </div>
                                                    )}
                                                </div>
                                                {/* The script itself, under the command line that runs it. Without this the
                                                    Commands field tells an engineer to run a file they cannot see — and
                                                    generation can produce several files, so it is a list, not one box. */}
                                                {taskHasCode(task.taskNo) && (
                                                    <div className="rounded-md border bg-muted/20 p-3">
                                                        <div className="flex items-center gap-1.5 text-xs font-medium mb-2">
                                                            <FileCode className="h-3.5 w-3.5 text-purple-600 dark:text-purple-400" />
                                                            {t('objects.runbook.generatedFiles', 'Generated files')}
                                                        </div>
                                                        {codeForTask(task.taskNo) ? (
                                                            <TaskCodeFiles set={codeForTask(task.taskNo) as TaskCodeSetLike} compact />
                                                        ) : (
                                                            <Button
                                                                type="button"
                                                                variant="outline"
                                                                size="sm"
                                                                className="h-7"
                                                                onClick={() => setCodeDialogTaskNo(task.taskNo as number)}
                                                            >
                                                                <FileCode className="h-3.5 w-3.5 mr-1" />
                                                                {t('objects.runbook.viewGeneratedFiles', 'View generated files')}
                                                            </Button>
                                                        )}
                                                    </div>
                                                )}
                                                {/* Image */}
                                                <div>
                                                    <label className="text-xs text-gray-500 block mb-1">{t('objects.runbook.image', 'Image')}</label>
                                                    <div className="flex items-center gap-2">
                                                        {task.imagePath ? (
                                                            <div className="relative group">
                                                                <img
                                                                    src={taskImageSrc(task.imagePath)}
                                                                    alt=""
                                                                    className="h-16 w-24 rounded border object-cover cursor-pointer hover:opacity-80"
                                                                    onClick={() => handleTaskChange(idx, 'imagePath', '__PICK__')}
                                                                />
                                                                <button
                                                                    type="button"
                                                                    className="absolute -top-1.5 -right-1.5 h-5 w-5 rounded-full bg-destructive text-destructive-foreground flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                                                                    onClick={() => handleTaskChange(idx, 'imagePath', null)}
                                                                >
                                                                    ×
                                                                </button>
                                                            </div>
                                                        ) : (
                                                            <Button
                                                                type="button"
                                                                variant="outline"
                                                                size="sm"
                                                                onClick={() => handleTaskChange(idx, 'imagePath', '__PICK__')}
                                                            >
                                                                <ImageIcon className="h-4 w-4 mr-1.5 text-blue-600" />
                                                                {t('objects.runbook.addImage', 'Add Image')}
                                                            </Button>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="flex flex-wrap gap-4">
                                                    <div>
                                                        <label className="text-xs text-gray-500 block mb-1">{t('objects.runbook.duration')}</label>
                                                        <Input
                                                            type="number"
                                                            value={task.duration ?? ''}
                                                            onChange={(e) => {
                                                                const v = e.target.value === '' ? null : parseInt(e.target.value, 10)
                                                                if (!isNaN(v as number)) handleTaskChange(idx, 'duration', v, false)
                                                            }}
                                                            onBlur={(e) => {
                                                                const v = e.target.value === '' ? 0 : parseInt(e.target.value, 10)
                                                                if (!isNaN(v)) handleTaskChange(idx, 'duration', v)
                                                            }}
                                                            className="w-16 h-8 text-sm"
                                                            placeholder="0"
                                                        />
                                                    </div>
                                                    <div className="flex-1 min-w-[180px]">
                                                        <label className="text-xs text-gray-500 block mb-1">{t('objects.runbook.dependencies')}</label>
                                                        {task.taskNo === 1 ? (
                                                            <Input value="" disabled className="h-8 text-sm" placeholder={t('objects.runbook.noDependencies')} />
                                                        ) : (
                                                            <>
                                                                <Input
                                                                    value={task.dependencies || ''}
                                                                    onChange={(e) => {
                                                                        handleTaskChange(idx, 'dependencies', e.target.value, false)
                                                                        const err = validateDependencies(e.target.value, tasksWithIndex, task.taskNo)
                                                                        setDependenciesError(err)
                                                                    }}
                                                                    onBlur={(e) => {
                                                                        const v = e.target.value.trim()
                                                                        const err = validateDependencies(v, tasksWithIndex, task.taskNo)
                                                                        if (err) {
                                                                            setDependenciesError(err)
                                                                            return
                                                                        }
                                                                        setDependenciesError(null)
                                                                        handleTaskChange(idx, 'dependencies', v)
                                                                    }}
                                                                    placeholder="1, 2"
                                                                    className={`h-8 text-sm ${dependenciesError ? 'border-red-500' : ''}`}
                                                                />
                                                                {dependenciesError && (
                                                                    <p className="text-xs text-red-500 mt-1">{dependenciesError}</p>
                                                                )}
                                                            </>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        ) : (
                                            /* Non-editing state */
                                            <>
                                                <div className="flex gap-3">
                                                    {/* Main content */}
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-start justify-between gap-2 mb-2">
                                                            {/* Wraps: the automation badge is always present now, so on a narrow
                                                                card the meta line must reflow rather than squeeze the dependencies. */}
                                                            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 min-w-0 flex-1">
                                                                <span className="flex items-center gap-1 text-sm font-semibold text-gray-900 dark:text-gray-100">
                                                                    <Timer className="h-3.5 w-3.5 text-green-500 flex-shrink-0" />
                                                                    {task.duration != null ? `${task.duration}${t('objects.runbook.minutesShort') || 'm'}` : '-'}
                                                                </span>
                                                                {task.commands?.trim() ? (
                                                                    <span className="flex items-center gap-1 text-xs text-muted-foreground">
                                                                        <Code2 className="h-3.5 w-3.5" />
                                                                        {t('objects.runbook.hasCommands') || 'Has commands'}
                                                                    </span>
                                                                ) : null}
                                                                {/* Named, not counted: the file in the chip is the one the
                                                                    command line runs, so the two read as a pair. */}
                                                                {taskHasCode(task.taskNo) && (
                                                                    <button
                                                                        type="button"
                                                                        onClick={(e) => { e.stopPropagation(); setCodeDialogTaskNo(task.taskNo as number) }}
                                                                        className="flex items-center gap-1 text-xs text-purple-700 dark:text-purple-300 hover:underline"
                                                                    >
                                                                        <FileCode className="h-3.5 w-3.5" />
                                                                        {(() => {
                                                                            const set = codeForTask(task.taskNo)
                                                                            const count = set?.files?.length ?? 0
                                                                            const entry = set?.entryFile
                                                                            if (entry) return count > 1 ? `${entry} +${count - 1}` : entry
                                                                            return t('objects.runbook.generatedFiles', 'Generated files')
                                                                        })()}
                                                                    </button>
                                                                )}
                                                                {/* Every card is tagged, manual included — an untagged card is
                                                                    indistinguishable from one whose tag failed to load. */}
                                                                <AutomationBadge mode={task.mode} method={task.method} />
                                                                <span className="text-xs text-muted-foreground">
                                                                    {t('objects.runbook.dependencies')}: {task.taskNo === 1 ? t('objects.runbook.noDependencies') : (task.dependencies || '-')}
                                                                </span>
                                                            </div>
                                                            {!readOnly && (
                                                                <div className="flex items-center gap-2 flex-shrink-0">
                                                                    {/* Add image button */}
                                                                    {!task.imagePath && (
                                                                        <button
                                                                            type="button"
                                                                            onClick={(e) => {
                                                                                e.stopPropagation()
                                                                                handleTaskChange(idx, 'imagePath', '__PICK__')
                                                                            }}
                                                                            className="h-7 w-7 rounded-md text-blue-600 hover:text-blue-700 hover:bg-blue-50 flex items-center justify-center transition-colors"
                                                                            title={t('objects.runbook.addImage', 'Add image')}
                                                                        >
                                                                            <ImageIcon className="h-4 w-4" />
                                                                        </button>
                                                                    )}
                                                                    <button
                                                                        type="button"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation()
                                                                            setDependenciesError(null)
                                                                            setEditingTaskIndex(editingTaskIndex === idx ? null : idx)
                                                                }}
                                                                className="p-1 rounded text-blue-600 hover:text-blue-700 transition-colors"
                                                                title={t('common.edit') || 'Edit'}
                                                                aria-label={t('common.edit') || 'Edit'}
                                                            >
                                                                <Pencil className="h-4 w-4" />
                                                            </button>
                                                            <button
                                                                type="button"
                                                                onClick={(e) => { e.stopPropagation(); setDeleteConfirmIndex(idx) }}
                                                                className="p-1 rounded text-red-500 hover:text-red-600 transition-colors"
                                                                title={t('common.delete') || 'Delete'}
                                                                aria-label={t('common.delete') || 'Delete'}
                                                            >
                                                                <Trash2 className="h-4 w-4" />
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                                        <p className="text-sm text-foreground">{task.task ? <TranslatableText text={task.task} /> : t('objects.runbook.task')}</p>
                                                        {task.commands?.trim() ? (
                                                            <details className="mt-3">
                                                                <summary className="text-xs text-gray-500 font-medium uppercase tracking-wide cursor-pointer">
                                                                    {t('objects.runbook.commands')}
                                                                </summary>
                                                                <pre className="mt-2 rounded-lg bg-gray-900 text-gray-100 p-3 font-mono text-sm whitespace-pre-wrap overflow-x-auto">
                                                                    {task.commands}
                                                                </pre>
                                                            </details>
                                                        ) : null}
                                                        {/* Directly beneath the command line, because they are one thing:
                                                            the command runs the entry file listed here. */}
                                                        {taskHasCode(task.taskNo) && (
                                                            <details className="mt-3">
                                                                <summary className="text-xs text-gray-500 font-medium uppercase tracking-wide cursor-pointer">
                                                                    {t('objects.runbook.generatedFiles', 'Generated files')}
                                                                </summary>
                                                                <div className="mt-2">
                                                                    {codeForTask(task.taskNo) ? (
                                                                        <TaskCodeFiles set={codeForTask(task.taskNo) as TaskCodeSetLike} compact />
                                                                    ) : (
                                                                        <Button
                                                                            type="button"
                                                                            variant="outline"
                                                                            size="sm"
                                                                            className="h-7"
                                                                            onClick={() => setCodeDialogTaskNo(task.taskNo as number)}
                                                                        >
                                                                            <FileCode className="h-3.5 w-3.5 mr-1" />
                                                                            {t('objects.runbook.viewGeneratedFiles', 'View generated files')}
                                                                        </Button>
                                                                    )}
                                                                </div>
                                                            </details>
                                                        )}
                                                    </div>
                                                    {/* Image on the right */}
                                                    {task.imagePath && (
                                                        <div className="flex-shrink-0 relative group">
                                                            <img
                                                                src={taskImageSrc(task.imagePath)}
                                                                alt=""
                                                                className="rounded-lg border w-32 h-24 object-cover cursor-pointer hover:opacity-90"
                                                                onClick={() => window.open(`/images/${task.imagePath}`, '_blank')}
                                                            />
                                                            {!readOnly && (
                                                                <button
                                                                    type="button"
                                                                    className="absolute -top-1.5 -right-1.5 h-5 w-5 rounded-full bg-destructive text-destructive-foreground flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                                                                    onClick={(e) => { e.stopPropagation(); handleTaskChange(idx, 'imagePath', null) }}
                                                                >
                                                                    ×
                                                                </button>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>
    )
    return (
        <div className="space-y-4">
            {/* Method recommendation — computed once per runbook display, renders
                nothing unless the backend had a confident answer. */}
            <MethodSuggestionBanner objectId={suggestionObjectId} masterId={suggestionMasterId} />

            {/* View toggle: Card | Table, plus the runbook-level automation summary so the
                answer to "does this runbook automate anything?" is visible without scrolling. */}
            <div className="flex flex-wrap items-center justify-between gap-4">
                <CardTableViewToggle
                    value={viewMode}
                    onValueChange={setViewMode}
                    cardLabel={t('objects.runbook.viewCard') || 'Card'}
                    tableLabel={t('objects.runbook.viewTable') || 'Table'}
                />
                <div className="flex flex-wrap items-center gap-3">
                    <AutomationSummary tasks={tasksWithIndex} />
                    {countDecisions(tasksWithIndex) > 0 && (
                        <>
                            <DecisionBadge
                                group={`${countDecisions(tasksWithIndex)} ${countDecisions(tasksWithIndex) === 1
                                    ? t('objects.runbook.decision', 'decision')
                                    : t('objects.runbook.decisions', 'decisions')}`}
                            />
                            <span className="text-sm text-muted-foreground">
                                {(() => {
                                    const r = runbookDurationRange(tasksWithIndex)
                                    return r.planned === r.worst
                                        ? formatMinutesToDHM(r.planned)
                                        : `${formatMinutesToDHM(r.planned)} – ${formatMinutesToDHM(r.worst)}`
                                })()}
                            </span>
                        </>
                    )}
                </div>
            </div>

            {/* Toolbar */}
            {!hideToolbar && (
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2">
                            <Switch
                                id="auto-update"
                                checked={autoUpdate}
                                onCheckedChange={onAutoUpdateChange}
                            />
                            <Label htmlFor="auto-update" className="text-sm">
                                {t('objects.runbook.autoUpdate')}
                            </Label>
                        </div>
                        {!autoUpdate && (
                            <>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={handleReset}
                                >
                                    <RotateCcw className="mr-2 h-4 w-4" />
                                    {t('objects.runbook.reset')}
                                </Button>
                                <Button
                                    variant="default"
                                    size="sm"
                                    onClick={() => {
                                        onSaveRef.current(localTasksRef.current)
                                        setHasUnsavedChanges(false)
                                    }}
                                    disabled={isSaving}
                                >
                                    <Save className="mr-2 h-4 w-4" />
                                    {isSaving ? t('objects.runbook.saving') : t('objects.runbook.save')}
                                </Button>
                            </>
                        )}
                        {!readOnly && (
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={handleAddTask}
                    >
                        <Plus className="mr-2 h-4 w-4" />
                        {t('objects.runbook.addTask')}
                    </Button>
                        )}
                        {onAiAssist && !readOnly && (
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={onAiAssist}
                                className="border-purple-300 text-purple-700 hover:bg-purple-50 dark:text-purple-300"
                            >
                                <AiSparkle size={16} className="mr-2" />
                                {t('objects.runbook.improveWithAi', 'Improve with Clara')}
                            </Button>
                        )}
                        {insertBlocked && (
                            <span className="text-xs text-amber-700 dark:text-amber-400">
                                {t('objects.runbook.insertBlockedHint', 'Save to re-enable inserting a task above')}
                            </span>
                        )}
                    </div>
                    {(onReplaceWithMaster || onCloneRunbook || onSaveAsMaster) && (
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button
                                    type="button"
                                    variant="outline"
                                >
                                    {t('objects.runbookOptions')}
                                    <ChevronDown className="ml-2 h-4 w-4" />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                                {onReplaceWithMaster && (
                                    <DropdownMenuItem onClick={onReplaceWithMaster}>
                                        {t('objects.replaceWithMaster')}
                                    </DropdownMenuItem>
                                )}
                                {onCloneRunbook && (
                                    <DropdownMenuItem onClick={onCloneRunbook}>
                                        {t('objects.cloneRunbook')}
                                    </DropdownMenuItem>
                                )}
                                {onSaveAsMaster && (
                                    <DropdownMenuItem onClick={onSaveAsMaster}>
                                        {t('objects.saveAsMaster')}
                                    </DropdownMenuItem>
                                )}
                            </DropdownMenuContent>
                        </DropdownMenu>
                    )}
                </div>
            )}
            {hideToolbar && !readOnly && (
                <div className="flex justify-end gap-2">
                        {onAiAssist && !readOnly && (
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={onAiAssist}
                                className="border-purple-300 text-purple-700 hover:bg-purple-50 dark:text-purple-300"
                            >
                                <AiSparkle size={16} className="mr-2" />
                                {t('objects.runbook.improveWithAi', 'Improve with Clara')}
                            </Button>
                        )}
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={handleAddTask}
                    >
                        <Plus className="mr-2 h-4 w-4" />
                        {t('objects.runbook.addTask')}
                    </Button>
                </div>
            )}

            {viewMode === 'card' && (
                <div className="max-w-3xl">
                    {tasksWithIndex.length === 0 ? (
                        <div className="rounded-lg border p-8 text-center text-muted-foreground">
                            {t('objects.noRunbookSelected')}
                        </div>
                    ) : (
                        <div className="relative pl-6">
                        <div
                            className="absolute left-[43px] top-5 bottom-5 w-0.5 bg-gray-200"
                            aria-hidden
                        />
                        <div className="space-y-4">
                            {buildRunbookEntries(tasksWithIndex).map((entry, entryIdx) =>
                                entry.type === 'task' ? (
                                    renderTaskCard(entry.task, entry.index)
                                ) : (
                                    <OptionGroupBlock
                                        key={`group-${entry.group}-${entryIdx}`}
                                        entry={entry}
                                        readOnly={readOnly}
                                        renderTaskCard={renderTaskCard}
                                    />
                                )
                            )}
                        </div>
                        {/* Add new task button at bottom */}
                        {!readOnly && (
                            <div className="mt-6 flex justify-center">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={handleAddTask}
                                    className="border-dashed"
                                >
                                    <Plus className="mr-2 h-4 w-4" />
                                    {t('objects.runbook.addNewTask') || 'Add new task'}
                                </Button>
                            </div>
                        )}
                    </div>
                    )}
                </div>
            )}
            {viewMode === 'table' && (
            <>
            <div className="rounded-md border overflow-visible runbook-table-wrapper">
                <Table className="w-full min-w-full table-auto">
                    <TableHeader>
                        {table.getHeaderGroups().map((headerGroup) => (
                            <TableRow key={headerGroup.id} className="bg-table-header border-b border-table-border">
                                {headerGroup.headers.map((header) => (
                                    <TableHead
                                        key={header.id}
                                        className="font-semibold text-foreground"
                                        style={{
                                            paddingTop: 'var(--table-header-padding-y)',
                                            paddingBottom: 'var(--table-header-padding-y)',
                                            paddingLeft: 'var(--table-header-padding-x)',
                                            paddingRight: 'var(--table-header-padding-x)',
                                            width: header.getSize() !== 150 ? `${header.getSize()}px` : undefined,
                                        }}
                                    >
                                        {header.isPlaceholder
                                            ? null
                                            : flexRender(header.column.columnDef.header, header.getContext())}
                                    </TableHead>
                                ))}
                            </TableRow>
                        ))}
                    </TableHeader>
                    <TableBody>
                        {table.getRowModel().rows?.length ? (
                            table.getRowModel().rows.map((row, rowIdx) => {
                                // A decision announces itself with a banner row above its first
                                // branch task, and every task in the group carries a coloured
                                // left border. Three cues, because one is not enough to read a
                                // grouping in a dense grid.
                                const group = (row.original.optionGroup || '').trim()
                                const name = (row.original.optionName || '').trim()
                                const inGroup = !!group && !!name
                                const prev = rowIdx > 0 ? table.getRowModel().rows[rowIdx - 1].original : null
                                const prevGroup = ((prev?.optionGroup || '').trim())
                                const startsGroup = inGroup && prevGroup !== group
                                const branchColour = !inGroup
                                    ? undefined
                                    : row.original.optionDefault
                                        ? '#1D9E75'
                                        : 'var(--border-strong, #B4B2A9)'
                                return (
                                <>
                                {startsGroup && (
                                    <TableRow key={`${row.id}-decision`} className="bg-blue-50 dark:bg-blue-950/20 hover:bg-blue-50 dark:hover:bg-blue-950/20">
                                        <TableCell
                                            colSpan={columns.length}
                                            style={{
                                                paddingTop: 'var(--table-cell-padding-y)',
                                                paddingBottom: 'var(--table-cell-padding-y)',
                                                paddingLeft: 'var(--table-cell-padding-x)',
                                                paddingRight: 'var(--table-cell-padding-x)',
                                            }}
                                        >
                                            <span className="inline-flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-blue-800 dark:text-blue-300">
                                                <span className="inline-flex items-center gap-2 font-medium">
                                                    <GitBranch className="h-4 w-4" aria-hidden />
                                                    {group} — {t('objects.runbook.chooseOne', 'choose one')}
                                                </span>
                                                {(() => {
                                                    // Same summary the card block carries: each branch and what it costs,
                                                    // so the trade is on screen in both views rather than only one.
                                                    const entry = buildRunbookEntries(tasksWithIndex)
                                                        .find((e) => e.type === 'group' && e.group === group) as
                                                        Extract<ReturnType<typeof buildRunbookEntries>[number], { type: 'group' }> | undefined
                                                    if (!entry) return null
                                                    return (
                                                        <span className="opacity-80">
                                                            {entry.branches
                                                                .map((b) => `${b.name} ${formatMinutesToDHM(b.duration)}${b.isDefault ? ` (${t('objects.runbook.plannedBranch', 'planned')})` : ''}`)
                                                                .join('  ·  ')}
                                                        </span>
                                                    )
                                                })()}
                                            </span>
                                        </TableCell>
                                    </TableRow>
                                )}
                                <TableRow key={row.id} className={inGroup ? 'bg-blue-50/40 dark:bg-blue-950/10 hover:bg-blue-50/60' : 'hover:bg-muted/50'}>
                                    {row.getVisibleCells().map((cell, cellIdx) => (
                                        <TableCell
                                            key={cell.id}
                                            style={{
                                                paddingTop: 'var(--table-cell-padding-y)',
                                                paddingBottom: 'var(--table-cell-padding-y)',
                                                paddingLeft: 'var(--table-cell-padding-x)',
                                                paddingRight: 'var(--table-cell-padding-x)',
                                                width: cell.column.getSize() !== 150 ? `${cell.column.getSize()}px` : undefined,
                                                overflow: 'visible',
                                                verticalAlign: 'top',
                                                // Single-sided border: square corners, per the design rules.
                                                borderLeft: cellIdx === 0 && branchColour ? `4px solid ${branchColour}` : undefined,
                                            }}
                                            className="overflow-visible"
                                        >
                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                        </TableCell>
                                    ))}
                                </TableRow>
                                </>
                                )
                            })
                        ) : (
                            <TableRow>
                                <TableCell colSpan={columns.length} className="h-24 text-center">
                                    {t('common.noResults')}
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </div>
            <style>{`
                .runbook-table-wrapper .overflow-auto {
                    overflow: visible !important;
                }
                .runbook-table-wrapper table {
                    table-layout: auto !important;
                }
            `}</style>
            </>
            )}

            {/* Delete confirmation dialog */}
            <Dialog open={deleteConfirmIndex !== null} onOpenChange={(open) => !open && setDeleteConfirmIndex(null)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{t('objects.runbook.deleteTask') || 'Delete Task'}</DialogTitle>
                        <DialogDescription>
                            {t('objects.runbook.deleteTaskConfirm') || 'Are you sure you want to delete this task? This action cannot be undone.'}
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDeleteConfirmIndex(null)}>
                            {t('common.cancel') || 'Cancel'}
                        </Button>
                        <Button variant="destructive" onClick={handleDeleteConfirm}>
                            {t('common.delete') || 'Delete'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Generated files for a saved task, fetched on demand rather than with the runbook —
                a runbook can carry twenty scripts and almost nobody opens all of them. */}
            <TaskCodeDialog
                open={codeDialogTaskNo != null}
                onOpenChange={(o) => { if (!o) setCodeDialogTaskNo(null) }}
                taskNo={codeDialogTaskNo}
                taskText={localTasks.find((x) => x.taskNo === codeDialogTaskNo)?.task}
                set={codeForTask(codeDialogTaskNo)}
                onFetch={onFetchTaskCode}
            />

            {/* Task Image Picker */}
            <TaskImagePicker
                open={imagePickerOpen}
                onOpenChange={(open) => {
                    setImagePickerOpen(open)
                    if (!open) setImagePickerIndex(null)
                }}
                onSelect={(imagePath) => {
                    if (imagePickerIndex !== null) {
                        handleTaskChange(imagePickerIndex, 'imagePath', imagePath)
                    }
                }}
                currentImagePath={imagePickerIndex !== null ? localTasks[imagePickerIndex]?.imagePath : null}
            />
        </div>
    )
}

